import api, { Base, Tags } from 'ev-api/api';
import { sanitizeAndTransformResponse } from 'ev-api/common/transformers';

import {
  DeletePhoneNumberFromPracticeParams,
  GetAllPhoneNumbersByCustomerParams,
  SavePhoneNumberToPracticeParams,
  ToggleInboundDialingParams,
  UpdatePhoneNumberToPracticeParams,
} from './params';
import { TransformedGetPhoneNumbersByCustomerResponse } from './responses';
import { getPhoneNumbersByCustomerResponse } from './transformers';

const cdxPhoneNumbersApi = api.injectEndpoints({
  endpoints: builder => ({
    savePhoneNumberToPractice: builder.mutation<
      void,
      SavePhoneNumberToPracticeParams
    >({
      query: ({
        customerId,
        customerName,
        flowType,
        phoneNumber,
        practiceId,
        visitTypeId,
        twilioStudioFlowId,
      }) => {
        return {
          url: `${Base.V4}/cdx_proxy/config/modules/twilio_flow/instances`,
          method: 'POST',
          body: {
            customer_owner: {
              owner_type: 'evisit_customer',
              owner_identity: customerId,
              owner_name: customerName,
            },
            instance_owner: {
              owner_type: 'evisit_practice',
              owner_identity: practiceId,
            },
            params: {
              twilio_flow_type: flowType,
              phone_number: phoneNumber,
              visit_type_id: visitTypeId,
              twilio_studio_flow_id: twilioStudioFlowId,
            },
          },
        };
      },
      transformResponse: sanitizeAndTransformResponse,
      invalidatesTags: [Tags.AdminCustomerPhoneNumbers],
    }),
    updatePracticePhoneNumber: builder.mutation<
      void,
      UpdatePhoneNumberToPracticeParams
    >({
      query: ({
        instanceId,
        flowType,
        phoneNumber,
        visitTypeId,
        twilioStudioFlowId,
      }) => {
        return {
          url: `${Base.V4}/cdx_proxy/config/modules/twilio_flow/instances/${instanceId}`,
          method: 'PATCH',
          body: {
            params: {
              twilio_flow_type: flowType,
              phone_number: phoneNumber,
              visit_type_id: visitTypeId,
              twilio_studio_flow_id: twilioStudioFlowId,
            },
          },
        };
      },
      transformResponse: sanitizeAndTransformResponse,
      invalidatesTags: [Tags.AdminCustomerPhoneNumbers],
    }),
    deletePhoneNumberFromPractice: builder.mutation<
      void,
      DeletePhoneNumberFromPracticeParams
    >({
      query: ({ instanceId }) => {
        return {
          url: `${Base.V4}/cdx_proxy/config/modules/twilio_flow/instances/${instanceId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: [Tags.AdminCustomerPhoneNumbers],
    }),
    getAllPhoneNumbersByCustomer: builder.query<
      TransformedGetPhoneNumbersByCustomerResponse,
      GetAllPhoneNumbersByCustomerParams
    >({
      query: ({ customerId }) => {
        return {
          url: `${Base.V4}/cdx_proxy/config/modules/twilio_flow/instances`,
          params: {
            customer_owner_type: 'evisit_customer',
            customer_owner_identifier: customerId,
            page_size: 2000,
          },
        };
      },
      transformResponse: getPhoneNumbersByCustomerResponse,
      providesTags: [Tags.AdminCustomerPhoneNumbers],
    }),
    enableInboundDialingForNumber: builder.mutation<
      void,
      ToggleInboundDialingParams
    >({
      query: ({ phoneNumberId }) => {
        return {
          url: `${Base.V4}/cdx_proxy/config/modules/twilio_flow/instances/${phoneNumberId}/enable`,
          method: 'PATCH',
        };
      },
      invalidatesTags: [Tags.AdminCustomerPhoneNumbers],
    }),
    disableInboundDialingForNumber: builder.mutation<
      void,
      ToggleInboundDialingParams
    >({
      query: ({ phoneNumberId }) => {
        return {
          url: `${Base.V4}/cdx_proxy/config/modules/twilio_flow/instances/${phoneNumberId}/disable`,
          method: 'PATCH',
        };
      },
      invalidatesTags: [Tags.AdminCustomerPhoneNumbers],
    }),
  }),
});

export const {
  useSavePhoneNumberToPracticeMutation,
  useUpdatePracticePhoneNumberMutation,
  useDeletePhoneNumberFromPracticeMutation,
  useGetAllPhoneNumbersByCustomerQuery,
  useEnableInboundDialingForNumberMutation,
  useDisableInboundDialingForNumberMutation,
} = cdxPhoneNumbersApi;
